import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createOrganization,
  deleteOrganization,
  getOrganization,
  getOrganizationById,
  getOrganizations,
  updateOrganization,
  type District,
  type OrganizationFilters,
  type OrganizationWriteInput,
} from '../../services';

export const organizationKeys = {
  all: ['organizations'] as const,
  list: () => [...organizationKeys.all, 'list'] as const,
  paged: (filters: OrganizationFilters) =>
    [...organizationKeys.all, 'paged', filters] as const,
  details: () => [...organizationKeys.all, 'detail'] as const,
  detail: (id: number) => [...organizationKeys.details(), id] as const,
};

export interface OrgOption {
  label: string;
  value: string;
  id: number;
  publicId: string;
  is_provider?: boolean;
  can_be_invited_as_provider?: boolean;
}

/**
 * Lightweight org dropdown options — used across SMS for selectors.
 * Kept untouched so existing consumers don't churn.
 */
export function useOrganizations() {
  return useQuery({
    queryKey: organizationKeys.list(),
    queryFn: async () => {
      const response = await getOrganization();
      return (response?.results ?? []).map((org: District): OrgOption => ({
        label: org.name,
        value: org.public_id,
        id: org.id,
        publicId: org.public_id,
        is_provider: org.is_provider,
        can_be_invited_as_provider: org.can_be_invited_as_provider,
      }));
    },
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Paged list for the multi-tenant Organizations surface. Originally the
 * Admin > Directory > Organizations sub-tab; ADR-114 Phase 3
 * re-mounts the consumer on `VendorOrgSelector`.
 */
export function useOrganizationsList(filters: OrganizationFilters) {
  return useQuery({
    queryKey: organizationKeys.paged(filters),
    queryFn: () => getOrganizations(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useOrganization(id: number) {
  return useQuery({
    queryKey: organizationKeys.detail(id),
    queryFn: () => getOrganizationById(id),
    enabled: !!id,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OrganizationWriteInput) => createOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: OrganizationWriteInput }) =>
      updateOrganization(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteOrganization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}
